import { Checkbox, Form, Input } from "ant-design-vue";
import lodash from "lodash";
import { defineAsyncComponent, toRaw } from "vue";
import i18n from "../../i18n";
import store from "../../store";
import { download } from "../../utils/file";
import { scrollToHasError } from "../../utils/scroll";
import { filter } from "../../utils/search";
import { randomUuid } from "../../utils/util";
import {
    createFieldDateRange,
    createFieldDatetimeRange,
    createFieldInput,
    createFieldInputNumber,
    createFieldRadioGroup,
    createFieldSelect,
    createFieldTreeSelect,
    customUseForm
} from "../input/template-builder/template-builder.logic";
import moment from "moment";

const ImageUrl = defineAsyncComponent(() => import("./custom-render/ImageUrl"));
const Date = defineAsyncComponent(() => import("./custom-render/Date"));
const Number = defineAsyncComponent(() => import("./custom-render/Number"));
const TextTruncate = defineAsyncComponent(() => import("../text/truncate/Truncate"));
const CIcon = defineAsyncComponent(() => import("../icon/CIcon"));
const CSelect = defineAsyncComponent(() => import("../input/template-builder/select/CSelect"));
const CDatePicker = defineAsyncComponent(() => import("../input/template-builder/date-picker/CDatePicker"));
const CRadioGroup = defineAsyncComponent(() => import("../input/template-builder/radio-group/CRadioGroup"));
const Attachment = defineAsyncComponent(() => import("../input/template-builder/attachment/Attachment"));
const InputNumber = defineAsyncComponent(() => import("../input/template-builder/input-number/InputNumber"));

export const rowKey = record => {
    return record.id || record._id || JSON.stringify(record);
};

export const createPage = ({ props, data }) => {
    const { clientSide, visibleItem } = props;
    const { page } = data;
    if (!clientSide) {
        return page;
    }
    const where = page.where;
    const sort = page.sort;
    const size = page.size;
    const currentPage = page.page;
    const start = currentPage * size;
    let end = (currentPage + 1) * size;
    let items = filter({
        list: page.data.filter(d => d.isDelete !== true && visibleItem?.({ item: d })),
        where,
        sort
    });
    const totalSize = items.length;
    const totalPage = totalSize / size + (totalSize % size > 0 ? 1 : 0);
    if (end > items.length) end = items.length;
    items = items.slice(start, end);
    return {
        select: page.select,
        join: page.join,
        where: page.where,
        sort: page.sort,
        children: page.children,
        page: page.page,
        size: page.size,
        totalPage: totalPage,
        totalSize: totalSize,
        isRecursive: page.isRecursive,
        isChildrenDataOnly: page.isChildrenDataOnly,
        data: items
    };
};

export const createColumnDisplay = ({ props, data }) => {
    data.columns.forEach(column => (column.sortOrder = false));
    const sort = data.page.sort?.[0];
    if (sort) {
        const sortType = sort.type === "DESC" ? "descend" : "ascend";
        const column = data.columns.find(column => column.dataIndex === sort.name);
        if (column) {
            column.sortOrder = sortType;
        }
    }
    if (props.visibleRowActions && props.rowActions.length > 0 && (!data.manageSelect || data.manageSelect.force)) {
        if (!data.columns.find(column => column.slots?.customRender === "action")) {
            data.columns.unshift({
                title: () => i18n.global.t("button.action"),
                slots: { customRender: "action" },
                width: props.rowActionWidth,
                align: "center",
                visible: () => true
            });
        }
    }
    return data.columns.filter(column => column.visible?.());
};

export const createColumnSearch = ({ data }) => {
    return data.columns.filter(column => column.search?.visible?.());
};

export const createPagination = ({ props, data, refs, emit, page }) => {
    if (!props.visiblePagination) {
        return null;
    }
    return {
        current: page.page + 1,
        pageSize: page.size,
        total: page.totalSize,
        pageSizeOptions: props.pageSizeOptions,
        showSizeChanger: true,
        size: "small",
        align: "right",
        showTotal: (total, range) => {
            return i18n.global.t("components.table.pagination.total", { total, range0: range[0] || 0, range1: range[1] || 0 });
        },
        onShowSizeChange: (current, size) => {
            data.page.page = current - 1;
            data.page.size = size;
            onClickSearch({ props, data, refs, emit });
        },
        onChange: (page, pageSize) => {
            data.page.page = page - 1;
            data.page.size = pageSize;
            onClickSearch({ props, data, refs, emit });
        }
    };
};

export const createTotalWidth = ({ columns }) => {
    return columns.length > 0 ? columns.reduce((total, d) => total + d.width) : 0;
};

export const onClickSearch = ({ props, data, refs, emit }) => {
    let promise = props.visibleSearch ? refs?.search?.data?.useForm?.validate?.() || Promise.resolve() : Promise.resolve();
    promise
        .then(() => {
            const columns = lodash.cloneDeep(toRaw(data.columns));
            const page = { ...clonePage({ props, page: data.page }), where: columnsToWhere({ columns }) };
            emit("update:page", page);
            emit("update:columns", columns);
            emit("search", { page, columns });
        })
        .catch(() => scrollToHasError());
};

export const onChangeTable = ({ props, data, refs, emit, pagination, sort }) => {
    Promise.resolve().then(() => {
        const page = data.page;
        if (pagination?.current) {
            page.page = pagination.current - 1;
        }
        if (pagination?.pageSize) {
            page.size = pagination.pageSize;
        }
        if (sort) {
            if (sort.order) {
                page.sort = [{ name: sort.field, type: sort.order === "descend" ? "DESC" : "ASC" }];
            } else {
                page.sort = props.sort;
            }
        }
    }).then(() => onClickSearch({ props, data, refs, emit }));
};

export const columnsToWhere = ({ columns }) => {
    return columns
        .filter(column => column.search?.name && column.search?.type && column.search?.operator && column.search?.field?.searchValue)
        .map(column => {
            let operator = column.search.getOperator ? column.search.getOperator({ value: column.search.field.searchValue }) : column.search.operator;
            let value = column.search.getValue ? column.search.getValue({ value: column.search.field.searchValue }) : column.search.field.searchValue;
            let value2 = column.search.getValue2 ? column.search.getValue2({ value: column.search.field.searchValue }) : null;
            if (operator === "between") {
                if (value && !value2) {
                    operator = ">=";
                }
            }
            if (operator === "in") {
                if (value.length === 0) {
                    return null;
                }
            } else {
                if (value === null) {
                    return null;
                }
            }
            return {
                name: column.search.name,
                type: column.search.type,
                operator,
                value,
                value2
            };
        })
        .filter(d => d !== null);
};

export const clonePage = ({ props, page }) => {
    const newPage = lodash.cloneDeepWith(toRaw(page), (value, key) => {
        if (key === "data") {
            return props.page.data;
        }
    });
    newPage.data.forEach(d => (d._id = d.id || d._id || randomUuid()));
    return newPage;
};

export const init = ({ props, data }) => {
    props.columns.filter(column => !column.id).forEach(column => (column.id = randomUuid()));
    props.columns
        .filter(column => !column.search?.field?.key)
        .forEach(column => {
            if (!column.search) column.search = {};
            if (!column.search.field) column.search.field = {};
        });
    data.columns = lodash.cloneDeep(toRaw(props.columns));
    data.page = clonePage({ props, page: props.page });
    data.options = props.options;
};

export const initRowSelection = ({ props, data, refs }) => {
    const type = data.manageSelect?.type || "checkbox";
    data.rowSelection = {
        selectedRowKeys: [],
        selectedRows: [],
        type,
        onChange: (selectedRowKeysParams, selectedRowsParams) => {
            data.rowSelection.selectedRowKeys = selectedRowKeysParams;
            if (type === "checkbox") {
                createPage({ props, data }).data.forEach(d => {
                    const selectedRow = selectedRowsParams.find(d2 => d._id === d2._id);
                    if (selectedRow) {
                        if (!data.rowSelection.selectedRows.find(d2 => d._id === d2._id)) {
                            data.rowSelection.selectedRows.push(selectedRow);
                        }
                    } else {
                        data.rowSelection.selectedRows = data.rowSelection.selectedRows.filter(d2 => d._id !== d2._id);
                    }
                });
            } else {
                data.rowSelection.selectedRows = selectedRowsParams;
            }
            if (data.manageSelect.autoOk) {
                refs.operator1.onClickManageOk({ props: refs.operator1.props });
            }
        },
        onChangeCustom: ({ checked, record, rowKey }) => {
            if (checked) {
                data.rowSelection.selectedRows.push(record);
            } else {
                data.rowSelection.selectedRows = data.rowSelection.selectedRows.filter(d => d._id !== record._id);
            }
            data.rowSelection.selectedRowKeys = data.rowSelection.selectedRows.map(d => rowKey(d));
        },
        getCheckboxProps: record => {
            return data.manageSelect?.getCheckboxProps?.({ record });
        }
    };
};

export const createColumnImageUrl = options => {
    return lodash.merge(
        {
            customRender: ({ text }) => <ImageUrl url={text}></ImageUrl>,
            width: 100,
            align: "center",
            visible: () => true,
            search: {
                visible: () => false
            }
        },
        options
    );
};

export const createColumnInput = options => {
    return lodash.merge(
        {
            sorter: true,
            width: 150,
            align: "left",
            visible: () => true,
            search: {
                visible: () => true,
                field: createFieldInput({
                    colProp: () => ({ xs: 24, md: 12 }),
                    label: () => options?.title?.(),
                    inputProp: ({ field }) => {
                        const inputProp = options?.inputProp?.({ field });
                        return { value: field.searchValue, "onUpdate:value": value => (field.searchValue = value), ...inputProp };
                    }
                }),
                name: options?.dataIndex,
                type: "String",
                operator: "like"
            }
        },
        options
    );
};

export const createColumnDate = options => {
    return lodash.merge(
        {
            customRender: param => <Date {...param.column} {...param}></Date>,
            sorter: true,
            width: 150,
            align: "center",
            visible: () => true,
            search: {
                visible: () => true,
                field: createFieldDateRange({
                    colProp: () => ({ xs: 24, md: 12 }),
                    label: () => options?.title?.(),
                    inputProp: ({ field }) => {
                        const inputProp = options?.inputProp?.({ field });
                        return { value: field.searchValue, "onUpdate:value": value => (field.searchValue = value), ...inputProp };
                    }
                }),
                name: options?.dataIndex,
                type: "Date",
                operator: "between",
                getValue: ({ value }) => value[0],
                getValue2: ({ value }) => value[1]
            },
            format: "DD/MM/YYYY"
        },
        options
    );
};

export const createColumnDatetime = options => {
    return lodash.merge(
        {
            customRender: param => <Date {...param.column} {...param}></Date>,
            sorter: true,
            width: 150,
            align: "center",
            visible: () => true,
            search: {
                visible: () => true,
                field: createFieldDatetimeRange({
                    colProp: () => ({ xs: 24, md: 12 }),
                    label: () => options?.title?.(),
                    inputProp: ({ field }) => {
                        const inputProp = options?.inputProp?.({ field });
                        return { value: field.searchValue, "onUpdate:value": value => {
                                field.searchValue = value
                                if (field.searchValue?.length === 2) {
                                    const date1 = moment(field.searchValue[0], "YYYY-MM-DD[T]HH:mm:ss[Z]");
                                    const date2 = moment(field.searchValue[1], "YYYY-MM-DD[T]HH:mm:ss[Z]");
                                    date1.set({ hour: 0, minute: 0, second: 0 });
                                    date2.set({ hour: 23, minute: 59, second: 59 });
                                    field.searchValue[0] = date1;
                                    field.searchValue[1] = date2;
                                }
                            }, ...inputProp };
                    }
                }),
                name: options?.dataIndex,
                type: "Datetime",
                operator: "between",
                getValue: ({ value }) => value[0],
                getValue2: ({ value }) => value[1]
            },
            format: "DD/MM/YYYY HH:mm",
            showTime: { format: "HH:mm" }
        },
        options
    );
};

export const createColumnInputNumber = options => {
    return lodash.merge(
        {
            customRender: param => <Number {...param.column} {...param}></Number>,
            sorter: true,
            width: 150,
            align: "center",
            visible: () => true,
            search: {
                visible: () => true,
                field: createFieldInputNumber({
                    colProp: () => ({ xs: 24, md: 12 }),
                    label: () => options?.title?.(),
                    inputProp: ({ field }) => {
                        const inputProp = options?.inputProp?.({ field });
                        return { value: field.searchValue, "onUpdate:value": value => (field.searchValue = value), ...inputProp };
                    }
                }),
                name: options?.dataIndex,
                type: "String",
                operator: "like"
            }
        },
        options
    );
};

export const createColumnTextTruncate = options => {
    return lodash.merge(
        {
            customRender: ({ text }) => <TextTruncate tag="pre" tagClass="pre-wrap" text={JSON.stringify(text, null, 4)}></TextTruncate>,
            sorter: false,
            width: 150,
            align: "left",
            visible: () => true,
            search: {
                visible: () => true,
                field: createFieldInput({
                    colProp: () => ({ xs: 24, md: 12 }),
                    label: () => options?.title?.(),
                    inputProp: ({ field }) => {
                        const inputProp = options?.inputProp?.({ field });
                        return { value: field.searchValue, "onUpdate:value": value => (field.searchValue = value), ...inputProp };
                    }
                }),
                name: options?.dataIndex,
                type: "String",
                operator: "like"
            }
        },
        options
    );
};

export const createColumnSelect = options => {
    return lodash.merge(
        {
            sorter: true,
            width: 150,
            align: "center",
            visible: () => true,
            search: {
                visible: () => true,
                field: createFieldSelect({
                    colProp: () => ({ xs: 24, md: 12 }),
                    label: () => options?.title?.(),
                    inputProp: ({ field }) => {
                        const inputProp = options?.inputProp?.({ field });
                        return { value: field.searchValue, "onUpdate:value": value => (field.searchValue = value), ...inputProp };
                    }
                }),
                name: options?.dataIndex,
                type: "UUID",
                operator: "=",
                getValue: ({ value }) => value.id
            }
        },
        options
    );
};

export const createColumnRadioGroup = options => {
    return lodash.merge(
        {
            sorter: true,
            width: 150,
            align: "center",
            visible: () => true,
            search: {
                visible: () => true,
                field: createFieldRadioGroup({
                    colProp: () => ({ xs: 24, md: 12 }),
                    label: () => options?.title?.(),
                    inputProp: ({ field }) => {
                        const inputProp = options?.inputProp?.({ field });
                        return { value: field.searchValue, "onUpdate:value": value => (field.searchValue = value), ...inputProp };
                    }
                }),
                name: options?.dataIndex,
                type: "UUID",
                operator: "=",
                getValue: ({ value }) => value.id
            }
        },
        options
    );
};

export const createColumnTreeSelect = options => {
    return lodash.merge(
        {
            sorter: true,
            width: 150,
            align: "center",
            visible: () => true,
            search: {
                visible: () => true,
                field: createFieldTreeSelect({
                    colProp: () => ({ xs: 24, md: 12 }),
                    label: () => options?.title?.(),
                    inputProp: ({ field }) => {
                        const inputProp = options?.inputProp?.({ field });
                        return { value: field.searchValue, "onUpdate:value": value => (field.searchValue = value), ...inputProp };
                    }
                }),
                name: options?.dataIndex,
                type: "UUID",
                operator: "=",
                getValue: ({ value }) => value.id
            }
        },
        options
    );
};

export const createColumnAttachment = options => {
    return lodash.merge(
        {
            dataIndexValue: ({ record, column }) => lodash.get(record, `${column.dataIndex}.name`),
            customRender: ({ text }) => {
                return (
                    <a href="javascript:void(0)" onClick={() => download({ file: text })}>
                        {text?.name}
                    </a>
                );
            },
            sorter: false,
            width: 150,
            align: "left",
            visible: () => true,
            search: {
                visible: () => false
            }
        },
        options
    );
};

export const createColumnBoolean = options => {
    return lodash.merge(
        {
            customRender: ({ text }) => {
                return text ? <CIcon file="check.svg"></CIcon> : <span></span>;
            },
            sorter: true,
            width: 100,
            align: "center",
            visible: () => true,
            search: {
                visible: () => true,
                field: createFieldSelect({
                    colProp: () => ({ xs: 24, md: 12 }),
                    label: () => options?.title?.(),
                    inputProp: ({ field }) => {
                        const inputProp = options?.inputProp?.({ field });
                        return {
                            value: field.searchValue,
                            "onUpdate:value": value => (field.searchValue = value),
                            list: ({ searchQuery }) =>
                                store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "BOOLEAN", value: searchQuery }),
                            optLabel: "value",
                            ...inputProp
                        };
                    }
                }),
                name: options?.dataIndex,
                type: "Boolean",
                operator: "=",
                getValue: ({ value }) => value.key
            }
        },
        options
    );
};

export const createColumnInlineInput = options => {
    return lodash.merge(createColumnInput(options), {
        customRender: ({ record, column }) => {
            const dataIndex = options.dataIndex;
            const itemsKey = options.itemsKey;
            const items = options.items;
            const useForm = options.useForm;
            const itemIndex = items?.findIndex(d => d._id === record._id);
            const key = `${itemsKey}.${itemIndex}.${dataIndex}`;
            const customRenderInputProp = options.customRenderInputProp?.({ record, column, key });
            const validateInfo = useForm?.validateInfos?.[key];
            let wrapper = <div></div>;
            if (validateInfo) {
                wrapper = <Form.Item wrapperCol={{ xs: 24 }} {...validateInfo}></Form.Item>;
            }
            return (
                <wrapper>
                    <Input
                        value={lodash.get(record, dataIndex)}
                        onUpdate:value={value => {
                            record.isSave = true;
                            lodash.set(record, dataIndex, value);
                        }}
                        placeholder={options.title?.()}
                        {...customRenderInputProp}
                    ></Input>
                </wrapper>
            );
        }
    });
};

export const createColumnInlineInputPassword = options => {
    return lodash.merge(createColumnInput(options), {
        customRender: ({ record, column }) => {
            const dataIndex = options.dataIndex;
            const itemsKey = options.itemsKey;
            const items = options.items;
            const useForm = options.useForm;
            const itemIndex = items?.findIndex(d => d._id === record._id);
            const key = `${itemsKey}.${itemIndex}.${dataIndex}`;
            const customRenderInputProp = options.customRenderInputProp?.({ record, column, key });
            const validateInfo = useForm?.validateInfos?.[key];
            let wrapper = <div></div>;
            if (validateInfo) {
                wrapper = <Form.Item wrapperCol={{ xs: 24 }} {...validateInfo}></Form.Item>;
            }
            return (
                <wrapper>
                    <Input.Password
                        value={lodash.get(record, dataIndex)}
                        onUpdate:value={value => {
                            record.isSave = true;
                            lodash.set(record, dataIndex, value);
                        }}
                        placeholder={options.title?.()}
                        {...customRenderInputProp}
                    ></Input.Password>
                </wrapper>
            );
        }
    });
};

export const createColumnInlineInputNumber = options => {
    return lodash.merge(createColumnInputNumber(options), {
        customRender: ({ record, column }) => {
            const dataIndex = options.dataIndex;
            const itemsKey = options.itemsKey;
            const items = options.items;
            const useForm = options.useForm;
            const itemIndex = items?.findIndex(d => d._id === record._id);
            const key = options.key ? options.key({ record, column, index: itemIndex }) : `${itemsKey}.${itemIndex}.${dataIndex}`;
            const customRenderInputProp = options.customRenderInputProp?.({ record, column, key });
            const validateInfo = useForm?.validateInfos?.[key];
            let wrapper = <div></div>;
            if (validateInfo) {
                wrapper = <Form.Item wrapperCol={{ xs: 24 }} {...validateInfo}></Form.Item>;
            }
            return (
                <wrapper>
                    <InputNumber
                        value={lodash.get(record, dataIndex)}
                        onUpdate:value={value => {
                            record.isSave = true;
                            lodash.set(record, dataIndex, value);
                        }}
                        placeholder={options.title?.()}
                        {...customRenderInputProp}
                    ></InputNumber>
                </wrapper>
            );
        }
    });
};

export const createColumnInlineDate = options => {
    return lodash.merge(createColumnDate(options), {
        customRender: ({ record, column }) => {
            const dataIndex = options.dataIndex;
            const itemsKey = options.itemsKey;
            const items = options.items;
            const useForm = options.useForm;
            const itemIndex = items?.findIndex(d => d._id === record._id);
            const key = `${itemsKey}.${itemIndex}.${dataIndex}`;
            const customRenderInputProp = { format: column.format, ...options.customRenderInputProp?.({ record, column, key }) };
            const validateInfo = useForm?.validateInfos?.[key];
            let wrapper = <div></div>;
            if (validateInfo) {
                wrapper = <Form.Item wrapperCol={{ xs: 24 }} {...validateInfo}></Form.Item>;
            }
            return (
                <wrapper>
                    <CDatePicker
                        value={lodash.get(record, dataIndex)}
                        onUpdate:value={value => {
                            record.isSave = true;
                            lodash.set(record, dataIndex, value);
                        }}
                        {...customRenderInputProp}
                    ></CDatePicker>
                </wrapper>
            );
        }
    });
};

export const createColumnInlineDatetime = options => {
    return lodash.merge(createColumnDatetime(options), {
        customRender: ({ record, column }) => {
            const dataIndex = options.dataIndex;
            const itemsKey = options.itemsKey;
            const items = options.items;
            const useForm = options.useForm;
            const itemIndex = items?.findIndex(d => d._id === record._id);
            const key = `${itemsKey}.${itemIndex}.${dataIndex}`;
            const customRenderInputProp = { format: column.format, ...options.customRenderInputProp?.({ record, column, key }) };
            const validateInfo = useForm?.validateInfos?.[key];
            let wrapper = <div></div>;
            if (validateInfo) {
                wrapper = <Form.Item wrapperCol={{ xs: 24 }} {...validateInfo}></Form.Item>;
            }
            return (
                <wrapper>
                    <CDatePicker
                        value={lodash.get(record, dataIndex)}
                        onUpdate:value={value => {
                            record.isSave = true;
                            lodash.set(record, dataIndex, value);
                        }}
                        {...customRenderInputProp}
                    ></CDatePicker>
                </wrapper>
            );
        }
    });
};

export const createColumnInlineTextArea = options => {
    return lodash.merge(
        {
            customRender: ({ record, column }) => {
                const dataIndex = options.dataIndex;
                const itemsKey = options.itemsKey;
                const items = options.items;
                const useForm = options.useForm;
                const itemIndex = items?.findIndex(d => d._id === record._id);
                const key = `${itemsKey}.${itemIndex}.${dataIndex}`;
                const customRenderInputProp = options.customRenderInputProp?.({ record, column, key });
                const validateInfo = useForm?.validateInfos?.[key];
                let wrapper = <div></div>;
                if (validateInfo) {
                    wrapper = <Form.Item wrapperCol={{ xs: 24 }} {...validateInfo}></Form.Item>;
                }
                const inputProp = {
                    value: lodash.get(record, dataIndex),
                    "onUpdate:value": value => {
                        record.isSave = true;
                        lodash.set(record, dataIndex, value);
                    },
                    placeholder: options.title?.(),
                    ...customRenderInputProp
                };
                return (
                    <wrapper>
                        <Input.TextArea {...inputProp}></Input.TextArea>
                    </wrapper>
                );
            },
            sorter: false,
            width: 200,
            align: "left",
            visible: () => true,
            search: {
                visible: () => false
            }
        },
        options
    );
};

export const createColumnInlineSelect = options => {
    return lodash.merge(createColumnSelect(options), {
        customRender: ({ record, column }) => {
            const dataIndex = options.dataIndex;
            const itemsKey = options.itemsKey;
            const items = options.items;
            const useForm = options.useForm;
            const itemIndex = items?.findIndex(d => d._id === record._id);
            const key = `${itemsKey}.${itemIndex}.${dataIndex}`;
            const customRenderInputProp = options?.customRenderInputProp?.({ record, column, key });
            const validateInfo = useForm?.validateInfos?.[key];
            let wrapper = <div></div>;
            if (validateInfo) {
                wrapper = <Form.Item wrapperCol={{ xs: 24 }} {...validateInfo}></Form.Item>;
            }
            return (
                <wrapper>
                    <CSelect
                        value={lodash.get(record, dataIndex)}
                        onUpdate:value={value => {
                            record.isSave = true;
                            lodash.set(record, dataIndex, value);
                        }}
                        placeholder={options.title?.()}
                        {...customRenderInputProp}
                    ></CSelect>
                </wrapper>
            );
        },
        dataIndexValue: ({ record, column }) => {
            const dataIndex = options.dataIndex;
            const itemsKey = options.itemsKey;
            const items = options.items;
            const itemIndex = items?.findIndex(d => d._id === record._id);
            const key = `${itemsKey}.${itemIndex}.${dataIndex}`;
            const inputProp = options?.inputProp?.({ field: column.search?.field });
            const customRenderInputProp = options?.customRenderInputProp?.({ record, column, key });
            const optLabel = inputProp?.optLabel || customRenderInputProp?.optLabel;
            return lodash.get(record, `${dataIndex}.${optLabel}`);
        }
    });
};

export const createColumnInlineCheckbox = options => {
    return lodash.merge(
        {
            customRender: ({ record, column }) => {
                const dataIndex = options.dataIndex;
                const itemsKey = options.itemsKey;
                const items = options.items;
                const useForm = options.useForm;
                const itemIndex = items?.findIndex(d => d._id === record._id);
                const key = `${itemsKey}.${itemIndex}.${dataIndex}`;
                const customRenderInputProp = options?.customRenderInputProp?.({ record, column, key });
                const validateInfo = useForm?.validateInfos?.[key];
                let wrapper = <div></div>;
                if (validateInfo) {
                    wrapper = <Form.Item wrapperCol={{ xs: 24 }} {...validateInfo}></Form.Item>;
                }
                return (
                    <wrapper>
                        <Checkbox
                            checked={lodash.get(record, dataIndex)}
                            onUpdate:checked={checked => {
                                record.isSave = true;
                                lodash.set(record, dataIndex, checked);
                            }}
                            {...customRenderInputProp}
                        ></Checkbox>
                    </wrapper>
                );
            },
            sorter: true,
            width: 50,
            align: "center",
            visible: () => true,
            search: {
                visible: () => true,
                field: createFieldSelect({
                    colProp: () => ({ xs: 24, md: 12 }),
                    label: () => options?.title?.(),
                    inputProp: ({ field }) => {
                        const inputProp = options?.inputProp?.({ field });
                        return {
                            value: field.searchValue,
                            "onUpdate:value": value => (field.searchValue = value),
                            list: ({ searchQuery }) =>
                                store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "BOOLEAN", value: searchQuery }),
                            optLabel: "value",
                            ...inputProp
                        };
                    }
                }),
                name: options?.dataIndex,
                type: "Boolean",
                operator: "=",
                getValue: ({ value }) => value.key
            }
        },
        options
    );
};

export const createColumnInlineRadioGroup = options => {
    return lodash.merge(createColumnSelect(options), {
        customRender: ({ record, column }) => {
            const dataIndex = options.dataIndex;
            const itemsKey = options.itemsKey;
            const items = options.items;
            const useForm = options.useForm;
            const itemIndex = items?.findIndex(d => d._id === record._id);
            const key = options.key ? options.key({ record, column, index: itemIndex }) : `${itemsKey}.${itemIndex}.${dataIndex}`;
            const customRenderInputProp = options?.customRenderInputProp?.({ record, column, index: itemIndex, key });
            const validateInfo = useForm?.validateInfos?.[key];
            let wrapper = <div></div>;
            if (validateInfo) {
                wrapper = <Form.Item wrapperCol={{ xs: 24 }} {...validateInfo}></Form.Item>;
            }
            return (
                <wrapper>
                    <CRadioGroup
                        value={lodash.get(record, dataIndex)}
                        onUpdate:value={value => {
                            record.isSave = true;
                            lodash.set(record, dataIndex, value);
                        }}
                        {...customRenderInputProp}
                    ></CRadioGroup>
                </wrapper>
            );
        }
    });
};

export const createColumnInlineAttachment = options => {
    return lodash.merge(createColumnAttachment(options), {
        customRender: ({ record, column }) => {
            const dataIndex = options.dataIndex;
            const itemsKey = options.itemsKey;
            const items = options.items;
            const useForm = options.useForm;
            const itemIndex = items?.findIndex(d => d._id === record._id);
            const key = options.customKey ? options.customKey({ record, column, itemIndex }) : `${itemsKey}.${itemIndex}.${dataIndex}`;
            const customRenderInputProp = options?.customRenderInputProp?.({ record, column, index: itemIndex, key });
            const validateInfo = useForm?.validateInfos?.[key];
            let wrapper = <div></div>;
            if (validateInfo) {
                const customValidateInfo = customUseForm({ props: { useForm }, field: { key } });
                if (customValidateInfo.required) {
                    if (!validateInfo.label) {
                        customValidateInfo.label = " ";
                        customValidateInfo.colon = false;
                        customValidateInfo.labelCol = { xs: 1, class: "p-0" };
                        customValidateInfo.wrapperCol = { xs: 23 };
                    }
                }
                wrapper = <Form.Item wrapperCol={{ xs: 24 }} {...validateInfo} {...customValidateInfo}></Form.Item>;
            }
            return (
                <wrapper>
                    <Attachment
                        value={
                            !options.customKey
                                ? lodash.get(record, dataIndex)
                                : (dataIndex ? lodash.get(record, dataIndex) : record)?.size > 0
                                ? dataIndex
                                    ? lodash.get(record, dataIndex)
                                    : record
                                : null
                        }
                        onUpdate:value={value => {
                            record.isSave = true;
                            if (!options.customKey) {
                                if (!value) {
                                    value.url = null;
                                    value.path = null;
                                }
                                lodash.set(record, dataIndex, value);
                            } else {
                                const customRecord = dataIndex ? lodash.get(record, dataIndex) : record;
                                lodash.set(customRecord, "isSave", true);
                                if (!value) {
                                    lodash.set(customRecord, "url", null);
                                    lodash.set(customRecord, "path", null);
                                } else {
                                    lodash.set(customRecord, "group", value?.group);
                                    lodash.set(customRecord, "isUserFile", value?.isUserFile);
                                }
                                lodash.set(customRecord, "name", value?.name);
                                lodash.set(customRecord, "blob", value?.blob);
                                lodash.set(customRecord, "size", value?.size);
                                lodash.set(customRecord, "type", value?.type);
                            }
                        }}
                        fileListShowMode="horizontal"
                        {...customRenderInputProp}
                    ></Attachment>
                </wrapper>
            );
        },
        width: 200
    });
};
